/**
 * Advanced report: pure view logic (no React, no network). Filtering, sorting, grouping, the executive summary
 * (overall score, risk exposure, top priorities, quick wins), the 90-day roadmap board, progress KPIs, status actions
 * per role, Brain rule labels and the CSV export. Unit tested in AdvancedReport.test.tsx.
 */
import {
  FINDING_COLUMNS, FINDING_SOURCES, FINDING_STATUSES, FINDING_TRANSITIONS, OWNER_ONLY_STATUSES, REASON_REQUIRED_STATUSES,
  RESOLVABILITY_MODES, ROADMAP_SLOTS, ReportViewDefinitionSchema,
  type AdvancedFinding, type AdvancedReport, type FindingStatus, type ReportFilters, type ReportView, type ReportViewDefinition,
} from '@qm/contracts/audit-remediation';
import { EFFORTS, FIX_OWNERS, SEVERITIES, SIGNAL_IDS, type LocalizedText, type Severity } from '@qm/contracts/audit-analysis';

export type Loc = 'en' | 'ar';
export type Column = (typeof FINDING_COLUMNS)[number];
export type GroupBy = ReportViewDefinition['groupBy'];
export type Section = ReportViewDefinition['hiddenSections'][number];
export type SortField = ReportViewDefinition['sort']['field'];
export type Density = 'comfortable' | 'compact';

export const DEFAULT_DEFINITION: ReportViewDefinition = ReportViewDefinitionSchema.parse({});

/** Fixed (non-optional) columns: title is always shown, then the chosen ones. */
export const ALL_COLUMNS = FINDING_COLUMNS;

/** Filter dimensions, in toolbar order, with their allowed values. */
export const FILTER_DIMENSIONS = {
  severity: SEVERITIES,
  signal: SIGNAL_IDS,
  status: FINDING_STATUSES,
  source: FINDING_SOURCES,
  owner: FIX_OWNERS,
  resolvability: RESOLVABILITY_MODES,
  roadmapSlot: ROADMAP_SLOTS,
} as const;
export type FilterDimension = keyof typeof FILTER_DIMENSIONS;
export const FILTER_KEYS = Object.keys(FILTER_DIMENSIONS) as FilterDimension[];

/** Done for the customer: the fix is in. */
export const RESOLVED_STATUSES: readonly FindingStatus[] = ['applied', 'verified'];
/** No longer asks for work: resolved, dismissed or risk accepted. */
export const CLOSED_STATUSES: readonly FindingStatus[] = ['applied', 'verified', 'dismissed', 'accepted_risk'];
export const isClosed = (s: FindingStatus) => CLOSED_STATUSES.includes(s);

/** Text in the reader's language; Arabic falls back to English (caller marks lang="en"). */
export function textFor(t: LocalizedText | null | undefined, loc: Loc): { text: string; lang: Loc } | null {
  if (!t) return null;
  if (loc === 'ar' && t.ar) return { text: t.ar, lang: 'ar' };
  return { text: t.en, lang: 'en' };
}

// ---------------- filter / sort / group ----------------

export function activeFilterCount(f: ReportFilters): number {
  return FILTER_KEYS.reduce((n, k) => n + (f[k] as readonly string[]).length, 0) + (f.search ? 1 : 0);
}

export function filterFindings(findings: readonly AdvancedFinding[], f: ReportFilters): AdvancedFinding[] {
  const q = f.search?.trim().toLowerCase() ?? '';
  return findings.filter((x) => {
    for (const k of FILTER_KEYS) {
      const want = f[k] as readonly string[];
      if (!want.length) continue;
      const have = k === 'resolvability' ? x.resolvability.mode : (x[k] as string);
      if (!want.includes(have)) return false;
    }
    if (!q) return true;
    const hay = [x.id, x.title.en, x.title.ar ?? '', x.evidence.en, x.evidence.ar ?? '', x.fix.en, ...x.checkIds].join(' ').toLowerCase();
    return hay.includes(q);
  });
}

const rank = <T extends string>(list: readonly T[]) => Object.fromEntries(list.map((v, i) => [v, i])) as Record<T, number>;
const SEV = rank(SEVERITIES);
const EFF = rank(EFFORTS);
const SLOT = rank(ROADMAP_SLOTS);
const STAT = rank(FINDING_STATUSES);
const SIG = rank(SIGNAL_IDS);

/** Ascending comparators; `desc` flips. Severity ascending = least severe first (so `desc` puts critical first). */
const CMP: Record<SortField, (a: AdvancedFinding, b: AdvancedFinding) => number> = {
  priority: (a, b) => a.priority - b.priority,
  severity: (a, b) => SEV[b.severity] - SEV[a.severity],
  signal: (a, b) => SIG[a.signal] - SIG[b.signal],
  status: (a, b) => STAT[a.status] - STAT[b.status],
  effort: (a, b) => EFF[a.effort] - EFF[b.effort],
  roadmapSlot: (a, b) => SLOT[a.roadmapSlot] - SLOT[b.roadmapSlot],
  statusUpdatedAt: (a, b) => (a.statusUpdatedAt ?? '').localeCompare(b.statusUpdatedAt ?? ''),
  title: (a, b) => a.title.en.localeCompare(b.title.en),
};

/** Stable sort; ties fall back to priority desc then id, so the order never jitters. */
export function sortFindings(findings: readonly AdvancedFinding[], sort: ReportViewDefinition['sort']): AdvancedFinding[] {
  const dir = sort.dir === 'asc' ? 1 : -1;
  return [...findings].sort((a, b) => dir * CMP[sort.field](a, b) || b.priority - a.priority || a.id.localeCompare(b.id));
}

export interface Group { key: string; items: AdvancedFinding[] }

const GROUP_ORDER: Record<Exclude<GroupBy, 'none'>, readonly string[]> = {
  signal: SIGNAL_IDS, severity: SEVERITIES, status: FINDING_STATUSES, resolvability: RESOLVABILITY_MODES, roadmapSlot: ROADMAP_SLOTS,
  source: FINDING_SOURCES, owner: FIX_OWNERS,
};

export const groupValue = (f: AdvancedFinding, by: Exclude<GroupBy, 'none'>): string => (by === 'resolvability' ? f.resolvability.mode : (f[by] as string));

/** Groups in enum order; empty groups are dropped. `none` is one group with key ''. */
export function groupFindings(findings: readonly AdvancedFinding[], by: GroupBy): Group[] {
  if (by === 'none') return [{ key: '', items: [...findings] }];
  return GROUP_ORDER[by].map((key) => ({ key, items: findings.filter((f) => groupValue(f, by) === key) })).filter((g) => g.items.length);
}

/** The full pipeline used by the table and the CSV export. */
export function visibleFindings(report: Pick<AdvancedReport, 'findings'>, def: ReportViewDefinition): AdvancedFinding[] {
  return sortFindings(filterFindings(report.findings, def.filters), def.sort);
}

// ---------------- executive summary ----------------

/** Mean of the measured signal scores (rounded); null when nothing was measured. */
export function overallScore(report: Pick<AdvancedReport, 'signals'>): number | null {
  const s = report.signals.map((x) => x.score).filter((x): x is number => x !== null);
  return s.length ? Math.round(s.reduce((a, b) => a + b, 0) / s.length) : null;
}

const RISK_WEIGHT: Record<Severity, number> = { critical: 10, high: 6, medium: 3, low: 1 };
export type RiskLevel = 'low' | 'moderate' | 'elevated' | 'severe';

/**
 * Risk exposure: the severity-weighted share of findings that are still open (accepted risks count, they are still
 * exposure), 0-100. Level: any open regulatory critical -> severe.
 */
export function riskExposure(findings: readonly AdvancedFinding[]) {
  const live = (f: AdvancedFinding) => !RESOLVED_STATUSES.includes(f.status) && f.status !== 'dismissed';
  const total = findings.reduce((n, f) => n + RISK_WEIGHT[f.severity], 0);
  const open = findings.filter(live);
  const weight = open.reduce((n, f) => n + RISK_WEIGHT[f.severity], 0);
  const score = total ? Math.round((weight / total) * 100) : 0;
  const counts = Object.fromEntries(SEVERITIES.map((s) => [s, open.filter((f) => f.severity === s).length])) as Record<Severity, number>;
  const regulatory = open.filter((f) => f.regulatory).length;
  const accepted = findings.filter((f) => f.status === 'accepted_risk').length;
  const level: RiskLevel = (counts.critical && open.some((f) => f.regulatory && f.severity === 'critical')) || score >= 75 ? 'severe'
    : counts.critical || score >= 50 ? 'elevated' : score >= 25 ? 'moderate' : 'low';
  return { score, level, counts, regulatory, accepted, open: open.length };
}

const byPriority = (a: AdvancedFinding, b: AdvancedFinding) => b.priority - a.priority || a.id.localeCompare(b.id);

/** The `n` highest-priority findings that still need work. */
export const topPriorities = (findings: readonly AdvancedFinding[], n = 5) => findings.filter((f) => !isClosed(f.status)).sort(byPriority).slice(0, n);

/** Quick wins that still need work, highest priority first. */
export const quickWins = (findings: readonly AdvancedFinding[]) => findings.filter((f) => f.roadmapSlot === 'quick_win' && !isClosed(f.status)).sort(byPriority);

// ---------------- roadmap + progress ----------------

export const ROADMAP_COLUMNS = [
  { key: 'weeks_1_2', slot: 'quick_win' },
  { key: 'weeks_3_6', slot: 'days_30' },
  { key: 'weeks_7_12', slot: 'days_90' },
] as const;

export interface RoadmapColumn { key: string; items: AdvancedFinding[]; done: number; total: number; pct: number }

/** Kanban columns for the 90-day plan (+ `later`). Membership comes from report.roadmap, falling back to the finding's slot. */
export function roadmapBoard(report: Pick<AdvancedReport, 'findings' | 'roadmap'>): { columns: RoadmapColumn[]; later: RoadmapColumn } {
  const byId = new Map(report.findings.map((f) => [f.id, f]));
  const placed = new Set<string>();
  const col = (key: string, slot: (typeof ROADMAP_SLOTS)[number]): RoadmapColumn => {
    const ids = [...report.roadmap[slot], ...report.findings.filter((f) => f.roadmapSlot === slot).map((f) => f.id)];
    const items: AdvancedFinding[] = [];
    for (const id of ids) { const f = byId.get(id); if (f && !placed.has(id)) { placed.add(id); items.push(f); } }
    items.sort(byPriority);
    const done = items.filter((f) => isClosed(f.status)).length;
    return { key, items, done, total: items.length, pct: items.length ? Math.round((done / items.length) * 100) : 0 };
  };
  const columns = ROADMAP_COLUMNS.map((c) => col(c.key, c.slot));
  return { columns, later: col('later', 'later') };
}

export interface ProgressPoint { week: number; start: string; resolved: number; verified: number }

/**
 * Cumulative resolved (applied + verified) and verified counts per week since the report, up to `now` (max 13 weeks).
 * Uses each finding's last status time: a finding that is resolved now counts from the week it got there.
 */
export function progressSeries(report: Pick<AdvancedReport, 'findings' | 'generatedAt'>, now: string): ProgressPoint[] {
  const start = Date.parse(report.generatedAt);
  const end = Math.max(start, Date.parse(now));
  const WEEK = 7 * 24 * 3600 * 1000;
  const weeks = Math.min(13, Math.floor((end - start) / WEEK) + 1);
  const out: ProgressPoint[] = [];
  for (let w = 0; w < weeks; w++) {
    const until = start + (w + 1) * WEEK;
    const done = (statuses: readonly FindingStatus[]) => report.findings.filter((f) => statuses.includes(f.status) && f.statusUpdatedAt && Date.parse(f.statusUpdatedAt) < until).length;
    out.push({ week: w + 1, start: new Date(start + w * WEEK).toISOString(), resolved: done(RESOLVED_STATUSES), verified: done(['verified']) });
  }
  return out;
}

export function progressTotals(findings: readonly AdvancedFinding[]) {
  const n = (s: readonly FindingStatus[]) => findings.filter((f) => s.includes(f.status)).length;
  return {
    total: findings.length, open: n(['open', 'regressed', 'failed']), inProgress: n(['in_progress', 'awaiting_approval', 'applying']),
    resolved: n(RESOLVED_STATUSES), verified: n(['verified']), dismissed: n(['dismissed']), accepted: n(['accepted_risk']), regressed: n(['regressed']),
  };
}

// ---------------- status actions ----------------

export type StatusActionKind = 'start' | 'dismiss' | 'accept' | 'reopen' | 'pause';
export interface StatusAction { kind: StatusActionKind; to: FindingStatus; needsReason: boolean }

const OWNER_ROLES = ['customer_owner', 'customer_admin'];
const WRITE_ROLES = [...OWNER_ROLES, 'customer_member'];
export const canWriteFindings = (roles: readonly string[] | undefined) => !!roles?.some((r) => WRITE_ROLES.includes(r));
export const isOwnerRole = (roles: readonly string[] | undefined) => !!roles?.some((r) => OWNER_ROLES.includes(r));

/** Hand actions the signed-in role may take from this status (F0 FINDING_TRANSITIONS.customer + owner-only rule). */
export function statusActions(status: FindingStatus, roles: readonly string[] | undefined): StatusAction[] {
  if (!canWriteFindings(roles)) return [];
  const owner = isOwnerRole(roles);
  const to = FINDING_TRANSITIONS.customer[status] ?? [];
  const kindOf = (t: FindingStatus): StatusActionKind => (t === 'in_progress' ? 'start' : t === 'dismissed' ? 'dismiss' : t === 'accepted_risk' ? 'accept' : status === 'in_progress' ? 'pause' : 'reopen');
  return to.filter((t) => owner || !OWNER_ONLY_STATUSES.includes(t)).map((t) => ({ kind: kindOf(t), to: t, needsReason: REASON_REQUIRED_STATUSES.includes(t) }));
}

/** Client-side reason rule (mirrors UpdateFindingStatusRequestSchema). */
export const reasonValid = (to: FindingStatus, reason: string) => !REASON_REQUIRED_STATUSES.includes(to) || reason.trim().length >= 5;

// ---------------- views ----------------

/** The definition part of a saved view (what the table needs). */
export const definitionOf = (v: Pick<ReportView, keyof ReportViewDefinition>): ReportViewDefinition =>
  ({ filters: v.filters, sort: v.sort, groupBy: v.groupBy, hiddenSections: v.hiddenSections, columns: v.columns });

export const sameDefinition = (a: ReportViewDefinition, b: ReportViewDefinition) => JSON.stringify(a) === JSON.stringify(b);

// ---------------- Brain rule references in plain words ----------------

export type BrainKind = 'law' | 'opt' | 'bw' | 'rule';
/**
 * `law:healthcare/dha-sma-6.1` -> { kind: 'law', area: 'Healthcare', rule: 'DHA SMA 6.1' }.
 * Short acronym-like tokens are upper-cased; anything with a longer word reads as a sentence.
 */
export function brainRefLabel(ref: string): { kind: BrainKind; area: string | null; rule: string } {
  const m = /^([a-z]+):(.*)$/.exec(ref);
  const kind: BrainKind = m && (m[1] === 'law' || m[1] === 'opt' || m[1] === 'bw') ? m[1] : 'rule';
  const body = m ? m[2]! : ref;
  const parts = body.split('/').filter(Boolean);
  const words = (s: string) => s.split(/[-_]+/).filter(Boolean);
  const sentence = (s: string) => { const w = words(s).join(' '); return w.charAt(0).toUpperCase() + w.slice(1); };
  const ruleRaw = parts.length > 1 ? parts.slice(1).join(' ') : parts[0] ?? ref;
  const tokens = words(ruleRaw);
  const acronym = tokens.length > 0 && tokens.every((t) => !/[a-z]{5,}/i.test(t));
  const rule = acronym ? tokens.map((t) => t.toUpperCase()).join(' ') : sentence(ruleRaw);
  return { kind, area: parts.length > 1 ? sentence(parts[0]!) : null, rule };
}

// ---------------- CSV ----------------

/** Spreadsheet formula injection guard (=, +, -, @, tab, CR at the start of a cell). */
export function csvCell(v: unknown): string {
  let s = v === null || v === undefined ? '' : String(v);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * CSV of the given findings: id + title + the chosen columns + evidence/fix (the export is the full record, the
 * on-screen columns only decide the middle). Labels come from `label(key)` so the export follows the report language.
 */
export function toCsv(findings: readonly AdvancedFinding[], columns: readonly Column[], loc: Loc, label: (k: string) => string): string {
  const head = ['id', 'title', ...columns, 'evidence', 'impact', 'fix'];
  const val = (f: AdvancedFinding, c: Column): string => {
    switch (c) {
      case 'priority': return String(f.priority);
      case 'severity': return label(`audit-remediation.severity.${f.severity}`);
      case 'signal': return label(`audit-remediation.report.signal.${f.signal}`);
      case 'source': return label(`audit-remediation.source.${f.source}`);
      case 'status': return label(`audit-remediation.status.${f.status}`);
      case 'resolvability': return label(`audit-remediation.resolvability.${f.resolvability.mode}`);
      case 'owner': return label(`audit-remediation.report.owner.${f.owner}`);
      case 'effort': return label(`audit-remediation.report.effort.${f.effort}`);
      case 'roadmapSlot': return label(`audit-remediation.roadmap.${f.roadmapSlot}`);
      case 'checkIds': return f.checkIds.join(' ');
    }
  };
  const lines = [head.map((h) => csvCell(label(`audit-remediation.report.col.${h}`))).join(',')];
  for (const f of findings) {
    lines.push([f.id, textFor(f.title, loc)?.text, ...columns.map((c) => val(f, c)), textFor(f.evidence, loc)?.text, textFor(f.impact, loc)?.text, textFor(f.fix, loc)?.text].map(csvCell).join(','));
  }
  // BOM so spreadsheet apps read Arabic as UTF-8
  return `\uFEFF${lines.join('\r\n')}\r\n`;
}
